import {
  DislikeFilled,
  DislikeOutlined,
  LikeFilled,
  LikeOutlined,
} from '@ant-design/icons'
import {
  ASSISTANT_FEEDBACK_NOTE_MAX,
  ASSISTANT_FEEDBACK_REASONS,
  AssistantChatMessage,
  AssistantFeedbackDetails,
  AssistantFeedbackRating,
  AssistantFeedbackReason,
} from '@stores/assistantStore'
import { Button, Input } from 'antd'
import { observer } from 'mobx-react'
import { useState } from 'react'

import { useAssistantSession } from './AssistantSessionContext'
import { KW } from './ui'

interface Props {
  message: AssistantChatMessage
}

export const FEEDBACK_NOT_SAVED =
  'Your rating could not be saved. Please try again.'

/**
 * Good / bad answer. Pressing the button that is already marked takes the rating back. A bad
 * rating is sent at once and then offers a short form for the reason and an optional note,
 * which the user can skip.
 */
const FeedbackButtons = ({ message }: Props) => {
  const session = useAssistantSession()
  const [formOpen, setFormOpen] = useState(false)
  const [reason, setReason] = useState<AssistantFeedbackReason | null>(null)
  const [note, setNote] = useState('')
  const [sending, setSending] = useState(false)
  const [failed, setFailed] = useState(false)

  if (!message.id) {
    return null
  }
  const messageId = message.id
  const rating = message.feedbackRating
  // The form belongs to a bad rating: gone when the rating is, also when it was not stored.
  const showForm = formOpen && rating === -1

  const submit = async (
    value: AssistantFeedbackRating,
    details?: AssistantFeedbackDetails
  ) => {
    setFailed(false)
    const stored = await session.submitFeedback(messageId, value, details)
    if (!stored) {
      setFailed(true)
    }
    return stored
  }

  const closeForm = () => {
    setFormOpen(false)
    setReason(null)
    setNote('')
  }

  const onGood = () => {
    closeForm()
    submit(rating === 1 ? 0 : 1)
  }

  const onBad = () => {
    if (rating === -1) {
      closeForm()
      submit(0)
      return
    }
    setFormOpen(true)
    submit(-1)
  }

  const onSend = async () => {
    const comment = note.trim()
    setSending(true)
    const stored = await submit(-1, {
      ...(reason ? { reason } : {}),
      ...(comment ? { comment } : {}),
    })
    setSending(false)
    if (stored) {
      closeForm()
    }
  }

  return (
    <>
      <div className="flex gap-1">
        <Button
          type="text"
          size="small"
          title={rating === 1 ? 'Remove rating' : 'Good answer'}
          aria-label="Good answer"
          aria-pressed={rating === 1}
          icon={rating === 1 ? <LikeFilled /> : <LikeOutlined />}
          onClick={onGood}
        />
        <Button
          type="text"
          size="small"
          title={rating === -1 ? 'Remove rating' : 'Bad answer'}
          aria-label="Bad answer"
          aria-pressed={rating === -1}
          icon={rating === -1 ? <DislikeFilled /> : <DislikeOutlined />}
          onClick={onBad}
        />
      </div>
      {/* Always present, so that a screen reader announces the text when it appears. Last in
        the row, like the form below: neither may move a button the user is about to click. */}
      <span
        role="alert"
        className="order-last text-xs"
        style={{ color: KW.red }}
      >
        {failed ? FEEDBACK_NOT_SAVED : ''}
      </span>
      {showForm && (
        <div
          role="group"
          aria-label="What was wrong with this answer?"
          className="order-last mt-1 flex basis-full flex-col gap-2 rounded-lg p-2"
          style={{ background: KW.soft2, border: `1px solid ${KW.line}` }}
        >
          <div className="text-xs" style={{ color: KW.body }}>
            What was wrong? (optional)
          </div>
          <div className="flex flex-wrap gap-1">
            {ASSISTANT_FEEDBACK_REASONS.map(option => (
              <Button
                key={option.code}
                size="small"
                shape="round"
                type={reason === option.code ? 'primary' : 'default'}
                aria-pressed={reason === option.code}
                onClick={() =>
                  setReason(reason === option.code ? null : option.code)
                }
              >
                {option.label}
              </Button>
            ))}
          </div>
          <Input.TextArea
            aria-label="Note (optional)"
            placeholder="Add a note (optional)"
            value={note}
            maxLength={ASSISTANT_FEEDBACK_NOTE_MAX}
            autoSize={{ minRows: 2, maxRows: 4 }}
            onChange={event => setNote(event.target.value)}
          />
          <div className="flex justify-end gap-1">
            <Button size="small" type="text" onClick={closeForm}>
              Skip
            </Button>
            <Button
              size="small"
              type="primary"
              loading={sending}
              disabled={!reason && !note.trim()}
              onClick={onSend}
            >
              Send
            </Button>
          </div>
        </div>
      )}
    </>
  )
}

export default observer(FeedbackButtons)
